/**
 * Route metadata registry — THE source of truth for what the public web sees.
 *
 *   • RouteSeo (src/seo/RouteSeo.jsx) reads it at runtime to set <title>,
 *     description, canonical, robots, Open Graph / Twitter tags and JSON-LD.
 *   • scripts/seo/*.mjs read it at build time to generate sitemap.xml,
 *     robots.txt, the prerender list and the CloudFront route manifest.
 *   • src/seo/__tests__ assert its invariants (unique titles, canonical host,
 *     no indexable route without a description, …).
 *
 * Keep it pure data (no React, no JSX, no browser globals): Node imports it
 * directly. Only `SEO` objects from the two intro content modules are
 * imported, so their copy stays single-sourced.
 *
 * Entry shape
 * ───────────
 *   path        exact pathname ("/about"), a param pattern ("/verify/:token")
 *               or a prefix rule ("/superadmin/*").
 *   index       true  → indexable: canonical + full metadata + in sitemap
 *               false → "noindex, nofollow", no canonical, never in sitemap.
 *   title / description / ogTitle / ogDescription / ogImage / ogType
 *   canonicalPath   for aliases: the path this URL canonicalises to.
 *   breadcrumbs     [{ name, path }] rendered as BreadcrumbList (and used by
 *                   the <Breadcrumbs/> component where a page shows them).
 *   schema          list of schema builder names from src/seo/schema.js
 *   sitemap         { changefreq, sources: [repo paths whose git history gives
 *                   lastmod] } — only for index:true entries.
 *   prerender       true → scripts/seo/prerender.mjs snapshots it to static HTML
 *   tier            1 = flagship, 2 = strong page, 3 = supporting, 4 = app/utility
 */
import { SEO as STUDENT_INTRO_SEO } from '../BasicComponents/studentIntro/studentIntroContent';
import { SEO as COLLEGE_INTRO_SEO } from '../BasicComponents/collegeIntro/collegeIntroContent';
import { SITE_NAME, SITE_DESCRIPTION } from './siteConfig';

const FE = 'skillgenpro-frontend/src';

export const ROUTES = [
  /* ───────────── Tier 1 — flagship public pages ───────────── */
  {
    path: '/',
    index: true,
    tier: 1,
    title: 'The Skill Genie | AI-Powered Industry Readiness Platform',
    description:
      'AI-powered industry readiness platform: technology learning, real projects on GitHub, AI mentoring, mock interviews and resume review for students and colleges.',
    ogTitle: 'The Skill Genie — AI-Powered Industry Readiness Platform',
    ogDescription:
      'Learn. Build. Deploy. Succeed. Structured learning, real projects, AI assistance, mock interviews and resume preparation for students, graduates, professionals and colleges.',
    ogType: 'website',
    schema: ['organization', 'website', 'webpage'],
    sitemap: { changefreq: 'weekly', sources: [`${FE}/BasicComponents/Home.jsx`] },
    prerender: true,
  },
  {
    path: '/tsg-product-engg',
    index: true,
    tier: 1,
    title: STUDENT_INTRO_SEO.title,
    description: STUDENT_INTRO_SEO.description,
    ogTitle: STUDENT_INTRO_SEO.ogTitle,
    ogDescription: STUDENT_INTRO_SEO.ogDescription,
    ogImage: STUDENT_INTRO_SEO.ogImage,
    ogType: 'website',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'TSG Nexus Product Engineering', path: '/tsg-product-engg' },
    ],
    schema: ['organization', 'website', 'webpage', 'breadcrumbs', 'nexusProduct'],
    sitemap: {
      changefreq: 'monthly',
      sources: [`${FE}/BasicComponents/StudentIntroPage.jsx`, `${FE}/BasicComponents/studentIntro`],
    },
    prerender: true,
  },
  {
    path: '/tsg-collab',
    index: true,
    tier: 1,
    title: COLLEGE_INTRO_SEO.title,
    description: COLLEGE_INTRO_SEO.description,
    ogTitle: COLLEGE_INTRO_SEO.ogTitle,
    ogDescription: COLLEGE_INTRO_SEO.ogDescription,
    ogImage: COLLEGE_INTRO_SEO.ogImage,
    ogType: 'website',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'TSG Nexus for Colleges', path: '/tsg-collab' },
    ],
    schema: ['organization', 'website', 'webpage', 'breadcrumbs'],
    sitemap: {
      changefreq: 'monthly',
      sources: [`${FE}/BasicComponents/CollegeIntroPage.jsx`, `${FE}/BasicComponents/collegeIntro`],
    },
    prerender: true,
  },

  /* ───────────── Tier 2 — supporting public pages ───────────── */
  {
    path: '/about',
    index: true,
    tier: 2,
    title: 'About The Skill Genie (TSG) | Evolve X Innovations',
    description:
      'What The Skill Genie (TSG) is, who it serves, what TSG Nexus provides, and how it relates to Evolve X Innovations, ExiHire and ExiTracker.',
    ogType: 'website',
    breadcrumbs: [{ name: 'Home', path: '/' }, { name: 'About', path: '/about' }],
    schema: ['organization', 'website', 'aboutPage', 'breadcrumbs'],
    sitemap: { changefreq: 'monthly', sources: [`${FE}/BasicComponents/About.jsx`] },
    prerender: true,
  },
  {
    path: '/contact',
    index: true,
    tier: 2,
    title: 'Contact The Skill Genie | Evolve X Innovations',
    description:
      'Contact The Skill Genie team at Evolve X Innovations — email, phone, office address in Vijayawada, and a contact form for students, colleges and partners.',
    ogType: 'website',
    breadcrumbs: [{ name: 'Home', path: '/' }, { name: 'Contact', path: '/contact' }],
    schema: ['organization', 'website', 'contactPage', 'breadcrumbs'],
    sitemap: { changefreq: 'yearly', sources: [`${FE}/BasicComponents/Contact.jsx`] },
    prerender: true,
  },
  {
    path: '/book-demo',
    index: true,
    tier: 2,
    title: 'Book a Demo of TSG Nexus for Your College | The Skill Genie',
    description:
      'Request a live demo of TSG Nexus for your college: student projects, engineering workflows, mock interviews, faculty and placement dashboards, co-branded certificates.',
    ogType: 'website',
    breadcrumbs: [{ name: 'Home', path: '/' }, { name: 'Book a Demo', path: '/book-demo' }],
    schema: ['organization', 'website', 'webpage', 'breadcrumbs'],
    sitemap: { changefreq: 'yearly', sources: [`${FE}/BasicComponents/PublicBookDemo.jsx`] },
    prerender: true,
  },

  /* ───────────── Tier 3 — legal ───────────── */
  {
    path: '/privacy-policy',
    index: true,
    tier: 3,
    title: 'Privacy Policy | The Skill Genie',
    description: 'How The Skill Genie and Evolve X Innovations collect, use and protect personal data on theskillgenie.com and in the TSG apps.',
    ogType: 'website',
    breadcrumbs: [{ name: 'Home', path: '/' }, { name: 'Privacy Policy', path: '/privacy-policy' }],
    schema: ['organization', 'website', 'webpage', 'breadcrumbs'],
    sitemap: { changefreq: 'yearly', sources: [`${FE}/components/PrivacyPolicy.jsx`] },
    prerender: true,
  },
  {
    path: '/terms-of-service',
    index: true,
    tier: 3,
    title: 'Terms of Service | The Skill Genie',
    description: 'Terms and conditions for using The Skill Genie platform, TSG Nexus and related services provided by Evolve X Innovations.',
    ogType: 'website',
    breadcrumbs: [{ name: 'Home', path: '/' }, { name: 'Terms of Service', path: '/terms-of-service' }],
    schema: ['organization', 'website', 'webpage', 'breadcrumbs'],
    sitemap: { changefreq: 'yearly', sources: [`${FE}/components/TermsOfService.jsx`] },
    prerender: true,
  },
  {
    path: '/account-deletion',
    index: true,
    tier: 3,
    title: 'Delete Your Account | The Skill Genie',
    description: 'How to request deletion of your The Skill Genie account and associated personal data.',
    ogType: 'website',
    breadcrumbs: [{ name: 'Home', path: '/' }, { name: 'Account deletion', path: '/account-deletion' }],
    schema: ['organization', 'website', 'webpage', 'breadcrumbs'],
    sitemap: { changefreq: 'yearly', sources: [`${FE}/components/AccountDeletion.jsx`] },
    prerender: true,
  },

  /* ───────────── Aliases → canonical page (reachable, not indexed) ───────────── */
  { path: '/privacy', index: false, canonicalPath: '/privacy-policy', title: 'Privacy Policy | The Skill Genie' },
  { path: '/terms', index: false, canonicalPath: '/terms-of-service', title: 'Terms of Service | The Skill Genie' },
  { path: '/delete-account', index: false, canonicalPath: '/account-deletion', title: 'Delete Your Account | The Skill Genie' },
  /* legacy paths — CloudFront 301s them; the SPA Navigate is the fallback */
  { path: '/student-intro', index: false, redirectTo: '/tsg-product-engg' },
  { path: '/college-intro', index: false, redirectTo: '/tsg-collab' },

  /* ───────────── Public but not indexable (auth entry points, utilities) ───────────── */
  { path: '/login', index: false, title: 'Sign in | The Skill Genie' },
  { path: '/register', index: false, title: 'Create your account | The Skill Genie' },
  { path: '/usertype', index: false, title: 'Get started | The Skill Genie' },
  { path: '/collegesignup', index: false, title: 'Register your college | The Skill Genie' },
  { path: '/collegelogin', index: false, title: 'College sign in | The Skill Genie' },
  { path: '/forgot-password', index: false, title: 'Reset your password | The Skill Genie' },
  { path: '/reset-password/:token', index: false, title: 'Reset your password | The Skill Genie' },
  { path: '/collegeforgotpass', index: false },
  { path: '/collegereset/:token', index: false },
  { path: '/account-locked', index: false },
  { path: '/verify/:token', index: false }, // per-student certificate pages carry personal data
  { path: '/features', index: false, title: 'Features | The Skill Genie' }, // legacy copy with stale pricing — kept reachable, not indexed
  { path: '/thankyou', index: false },
  { path: '/payment-success', index: false },

  /* ───────────── Application surface (prefix rules) ───────────── */
  { path: '/dashboard/*', index: false },
  { path: '/profile/*', index: false },
  { path: '/edit-profile/*', index: false },
  { path: '/student/*', index: false },
  { path: '/admin/*', index: false },
  { path: '/admin1', index: false },
  { path: '/adminroles', index: false },
  { path: '/admintechgrid', index: false },
  { path: '/adminusersdashboard', index: false },
  { path: '/admincollegesdashboard', index: false },
  { path: '/admin-console/*', index: false },
  { path: '/superadmin/*', index: false },
  { path: '/superpayments', index: false },
  { path: '/bulkemailing', index: false },
  { path: '/supporttickets', index: false },
  { path: '/support-student', index: false },
  { path: '/support-college', index: false },
  { path: '/nextgen-builder/*', index: false },
  { path: '/nextgen/*', index: false },
  { path: '/college/*', index: false },
  { path: '/collegeabout', index: false },
  { path: '/college-analytics', index: false },
  { path: '/college-challenge', index: false },
  { path: '/collegehome/*', index: false },
  { path: '/CollegeViewProfile', index: false },
  { path: '/CollegeEditProfile', index: false },
  { path: '/TotalStudents', index: false },
  { path: '/StudentsProgress', index: false },
  { path: '/TopPerformers', index: false },
  { path: '/CollegeFAQs', index: false },
  { path: '/CollegeFeedback', index: false },
  { path: '/CollegeLogoutFromThisDevice', index: false },
  { path: '/CollegeLogoutFromAllDevices', index: false },
  { path: '/institutional-impact', index: false },
  { path: '/techgrid', index: false },
  { path: '/techmcq', index: false },
  { path: '/practice/*', index: false },
  { path: '/practices/*', index: false },
  { path: '/questions/*', index: false },
  { path: '/levelclearance', index: false },
  { path: '/code-hunt', index: false },
  { path: '/level1', index: false },
  { path: '/level2', index: false },
  { path: '/level3', index: false },
  { path: '/python', index: false },
  { path: '/leaderboard', index: false },
  { path: '/contest', index: false },
  { path: '/languagegrid', index: false },
  { path: '/compiler', index: false },
  { path: '/chatbot', index: false },
  { path: '/notes', index: false },
  { path: '/tsgmynotes', index: false },
  { path: '/tsgnotes', index: false },
  { path: '/tsg', index: false },
  { path: '/tsg/*', index: false },
  { path: '/explore', index: false },
  { path: '/explore/*', index: false },
  { path: '/launch', index: false },
  { path: '/launch/*', index: false },
  { path: '/launchpad/*', index: false },
  { path: '/launchpadpage', index: false },
  { path: '/skillgenlaunchpad', index: false },
  { path: '/skillgenlaunchpadlevels', index: false },
  { path: '/skillgenlaunchpadclearance', index: false },
  { path: '/skillgen-questions', index: false },
  { path: '/topicexplorer', index: false },
  { path: '/tech-tree-reader', index: false },
  { path: '/elite-mastery', index: false },
  { path: '/elite-mastery/*', index: false },
  { path: '/mockinterviewhome', index: false },
  { path: '/mockinterview/*', index: false },
  { path: '/mockinterviewpayments', index: false },
  { path: '/mockinterviewpayments_history', index: false },
  { path: '/interview-result', index: false },
  { path: '/premium-interview', index: false },
  { path: '/coding-interview', index: false },
  { path: '/bootcamp', index: false },
  { path: '/bootcamp/*', index: false },
  { path: '/bootcamp1', index: false },
  { path: '/bootcamp-selector', index: false },
  { path: '/bootcampselector', index: false },
  { path: '/individual-roles', index: false },
  { path: '/team-roles', index: false },
  { path: '/team-leaderboard', index: false },
  { path: '/role-dashboard', index: false },
  { path: '/individual-role-viewer', index: false },
  { path: '/team-role-replica', index: false },
  { path: '/techuserdashboard', index: false },
  { path: '/techbunchuserdashboard', index: false },
  { path: '/certifieduserdashboard', index: false },
  { path: '/certificate-demo', index: false },
  { path: '/certificate-preview', index: false },
  { path: '/voice-training', index: false },
  { path: '/payment', index: false },
  { path: '/subscription-plans', index: false },
  { path: '/skillgenpayment', index: false },
  { path: '/skillgenpayments', index: false },
  { path: '/landingpage1', index: false },
  { path: '/landingpage2', index: false },
];

/** Metadata applied when a pathname matches no entry (the React NotFound
 *  page, or any route someone adds without registering it). Unknown URLs must
 *  never be indexable — this is also the soft-404 safety net. */
export const UNMATCHED = {
  index: false,
  title: `Page not found | ${SITE_NAME}`,
  description: SITE_DESCRIPTION,
  notFound: true,
};

/** Fallback title for application routes that do not declare one. */
export const APP_DEFAULT_TITLE = SITE_NAME;
